import type { ApprovalInfo, ApprovalStatus } from "@fistos/shared/api";
import { ShieldCheck } from "lucide-solid";
import { createMemo, createResource, For, Show } from "solid-js";
import { toast } from "somoto";
import { ToneBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errorText } from "@/lib/api";
import { now } from "@/lib/clock";
import { ago, humanize } from "@/lib/format";
import { live } from "@/lib/live";

const TONE: Record<ApprovalStatus, "warn" | "good" | "bad" | "neutral"> = {
  pending: "warn",
  approved: "good",
  rejected: "bad",
  expired: "neutral",
};

export function ApprovalsPage() {
  const [history] = createResource(() => api.approvals(false));
  // History from the API, updated by live events.
  const rows = createMemo(() => {
    const byId = new Map<string, ApprovalInfo>((history() ?? []).map((a) => [a.id, a]));
    for (const a of Object.values(live.approvals)) byId.set(a.id, a);
    return [...byId.values()].sort((a, b) => b.createdAt - a.createdAt);
  });
  const decide = async (a: ApprovalInfo, approved: boolean) => {
    try {
      await api.decideApproval(a.id, approved);
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  return (
    <div class="space-y-6">
      <div>
        <h1 class="text-2xl font-semibold tracking-tight">Approvals</h1>
        <p class="text-muted-foreground text-sm">
          Actions the phones' policy broker held for you: payments, account changes, unknown recipients, factory reset
          and similar.
        </p>
      </div>
      <Show
        when={rows().length}
        fallback={
          <Card class="items-center gap-2 border-dashed p-12 text-center">
            <ShieldCheck class="text-muted-foreground size-8" />
            <div class="font-medium">Nothing has needed your approval yet</div>
          </Card>
        }
      >
        <Card class="overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Action</TableHead>
                <TableHead class="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <For each={rows()}>
                {(a) => (
                  <TableRow>
                    <TableCell class="text-muted-foreground whitespace-nowrap">{ago(a.createdAt, now())}</TableCell>
                    <TableCell class="whitespace-nowrap">
                      {live.devices[a.deviceId]?.name ?? a.deviceId.slice(0, 8)}
                    </TableCell>
                    <TableCell class="whitespace-nowrap">{humanize(a.category)}</TableCell>
                    <TableCell class="max-w-md">
                      <div class="truncate font-mono text-xs">{a.actionText}</div>
                      <div class="text-muted-foreground truncate text-xs">{a.reason}</div>
                    </TableCell>
                    <TableCell class="text-right">
                      <Show
                        when={a.status === "pending" && a.expiresAt > now()}
                        fallback={<ToneBadge tone={TONE[a.status]}>{a.status}</ToneBadge>}
                      >
                        <div class="flex justify-end gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => decide(a, false)}>
                            Decline
                          </Button>
                          <Button size="sm" onClick={() => decide(a, true)}>
                            Approve
                          </Button>
                        </div>
                      </Show>
                    </TableCell>
                  </TableRow>
                )}
              </For>
            </TableBody>
          </Table>
        </Card>
      </Show>
    </div>
  );
}
